import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { receiptsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const METHOD_LABELS = {
  cash: '💵 Cash',
  bank_transfer: '🏦 Bank Transfer',
  cheque: '📝 Cheque',
  online: '💻 Online',
  other: '🔖 Other'
};

export default function Receipts() {
  const { settings } = useSettings();
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 15;
  const cur = settings.currency || 'BDT';

  const fetchReceipts = useCallback(() => {
    setLoading(true);
    receiptsAPI.getAll({ search, page, limit })
      .then(res => { setReceipts(res.data.receipts); setTotal(res.data.total); })
      .catch(() => toast.error('Failed to load receipts'))
      .finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const handleDelete = async (id, num) => {
    if (!window.confirm(`Delete receipt ${num}?`)) return;
    try {
      await receiptsAPI.delete(id);
      toast.success('Receipt deleted');
      fetchReceipts();
    } catch { toast.error('Delete failed'); }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Receipts</h1>
        <Link to="/receipts/new" className="btn btn-primary">+ New Receipt</Link>
      </div>

      <div className="search-bar">
        <input
          className="form-control search-input"
          placeholder="Search by receipt no. or client..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : receipts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💳</div>
            <div className="empty-state-text">No receipts found</div>
            <Link to="/receipts/new" className="btn btn-primary" style={{ marginTop: 16 }}>Create First Receipt</Link>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="table-wrapper desktop-table">
              <table>
                <thead>
                  <tr>
                    <th>Receipt No.</th>
                    <th>Client</th>
                    <th>Bill No.</th>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map(r => (
                    <tr key={r.id}>
                      <td><span style={{ fontWeight: 700, color: '#10b981' }}>{r.receipt_number}</span></td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.client_name}</div>
                        {r.client_address && <div style={{ fontSize: 12, color: '#94a3b8' }}>{r.client_address}</div>}
                      </td>
                      <td>
                        {r.bill_id ? (
                          <Link to={`/bills/${r.bill_id}`} style={{ color: '#2563eb', fontWeight: 600, fontSize: 13 }}>{r.bill_number}</Link>
                        ) : <span style={{ color: '#94a3b8' }}>-</span>}
                      </td>
                      <td style={{ fontSize: 13, color: '#64748b' }}>{r.payment_date ? format(new Date(r.payment_date), 'dd MMM yyyy') : '-'}</td>
                      <td style={{ fontSize: 13 }}>{METHOD_LABELS[r.payment_method] || r.payment_method}</td>
                      <td style={{ fontWeight: 700, color: '#10b981' }}>{cur} {parseFloat(r.amount).toLocaleString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Link to={`/receipts/${r.id}`} className="btn btn-outline btn-sm">View</Link>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id, r.receipt_number)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobile-card-list" style={{ padding: '12px' }}>
              {receipts.map(r => (
                <div className="mobile-card" key={r.id}>
                  <div className="mobile-card-row">
                    <span style={{ fontWeight: 700, color: '#10b981', fontSize: 14 }}>{r.receipt_number}</span>
                    <span style={{ fontSize: 13, color: '#64748b' }}>{METHOD_LABELS[r.payment_method] || r.payment_method}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Client</span>
                    <span className="mobile-card-value">{r.client_name}</span>
                  </div>
                  {r.bill_number && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Bill</span>
                      <span className="mobile-card-value" style={{ color: '#2563eb', fontWeight: 600 }}>{r.bill_number}</span>
                    </div>
                  )}
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Date</span>
                    <span className="mobile-card-value">{r.payment_date ? format(new Date(r.payment_date), 'dd MMM yyyy') : '-'}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Amount</span>
                    <span className="mobile-card-value" style={{ fontWeight: 700, color: '#10b981' }}>{cur} {parseFloat(r.amount).toLocaleString()}</span>
                  </div>
                  <div className="mobile-card-actions">
                    <Link to={`/receipts/${r.id}`} className="btn btn-outline btn-sm">View</Link>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id, r.receipt_number)}>Del</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: 13, color: '#64748b' }}>{total} receipts</span>
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                className={`btn btn-sm ${page === i + 1 ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPage(i + 1)}
              >{i + 1}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}